import { assert, BadRequestError, codes } from '@fragment/error-utils';
import { getStringParametersInternal } from '@fragment/types/strings';

// Iterate through obj keys to fill in params. By default, does not require params under the "children" key to fill
export const fillParams = <T>(
  obj: T,
  params: Record<string, string> = {},
  options: { allowMissing?: boolean; allowMissingInChildren?: boolean } = {
    allowMissing: false,
    allowMissingInChildren: true,
  }
): T => {
  if (typeof obj === 'string') {
    // Do a one-pass parameterization of the string
    const tokens = obj.split(/(\{\{[^{}]+?\}\})/g);
    const missingParams = new Set<string>();
    const result = tokens
      .map((token) => {
        // Check if this token is a parameter or a regular string
        const match = token.match(/\{\{([^{}]+?)\}\}/);
        if (match) {
          const param = match[1];
          const paramValue = params[param];
          // Check for both undefined and null to prevent "null" strings
          if (paramValue === undefined || paramValue === null) {
            missingParams.add(param);
            return token;
          }
          return paramValue;
        }
        return token;
      })
      .join('');
    if (!options.allowMissing) {
      assert(!missingParams.size, BadRequestError, {
        message: `Failed to fill all parameters: ${obj} -> ${result}`,
        code: codes.parameterization_failed,
      });
    }
    return result as T;
  }
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      return obj.map((value) => fillParams(value, params, options)) as T;
    }
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        fillParams(value, params, {
          ...options,
          allowMissing:
            key === 'children'
              ? options.allowMissingInChildren
              : options.allowMissing,
        }),
      ])
    ) as T;
  }
  return obj;
};

export function getSchemaObjectParametersInternal(
  obj: any,
  parameters: Set<string>,
  includeChildren: boolean
): void {
  if (typeof obj === 'string') {
    getStringParametersInternal(obj, parameters);
  } else if (typeof obj === 'object') {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      // templatePath is only present on `account` in the frontend, and its parameters
      // are always auto-generated. The actual value we care about is `line.account.path`.
      if (key !== 'templatePath' && (key !== 'children' || includeChildren)) {
        getSchemaObjectParametersInternal(
          obj[key],
          parameters,
          includeChildren
        );
      }
    }
  }
}

/**
 * The parameters used in a schema object. By default, does not include parameters used in children.
 */
export function getSchemaObjectParameters(
  obj: any,
  includeChildren: boolean = false
): string[] {
  const params = new Set<string>();
  getSchemaObjectParametersInternal(obj, params, includeChildren);
  return Array.from(params);
}

export function canFillParams(
  obj: Record<string, unknown>,
  parameters: Record<string, string>
): boolean {
  const params = getSchemaObjectParameters(obj, true);
  return params.every((param) => parameters[param] !== undefined);
}
